import React, { useState, useEffect, useMemo } from 'react';
import { fetchPayslips_pending } from '../utils/api';
import PayslipModal from './PayslipModal';
import HelpButton from '../components/shared/HelpButton';
import ContractViewer from '../components/ContractViewer';


const pageStyle = {
  padding: '16px 18px 32px',
  width: '100%',
  boxSizing: 'border-box',
  color: '#f1f5f9',
};

const headerWrapStyle = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 12,
  marginBottom: 16,
};

const titleBlockStyle = { display: 'flex', flexDirection: 'column' };

const titleStyle = {
  fontSize: '1.4rem',
  fontWeight: 700,
  color: '#f1f5f9',
  letterSpacing: '-0.02em',
  margin: 0,
};

const subtitleStyle = {
  fontSize: '0.78rem',
  color: 'rgba(255,255,255,0.45)',
  marginTop: 2,
};

/* Stats card — same gradient bar pattern as MobileStatsCard */
const statsCardStyle = {
  background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
  border: '1px solid rgba(34,211,238,0.15)',
  borderRadius: 16,
  overflow: 'hidden',
  marginBottom: 18,
  position: 'relative',
};

const statsGradientBarStyle = {
  height: 3,
  background: 'linear-gradient(90deg, #22d3ee, #3b82f6, #8b5cf6)',
};

const statsInnerStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  padding: '16px 20px 6px',
  flexWrap: 'wrap',
  gap: 12,
};

const statsBlockStyle = { minWidth: 140 };

const statsLabelStyle = {
  fontSize: '0.7rem',
  fontWeight: 600,
  color: 'rgba(255,255,255,0.4)',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  marginBottom: 4,
};

const statsAmountStyle = {
  fontSize: '1.5rem',
  fontWeight: 800,
  color: '#f1f5f9',
  fontFamily: 'monospace',
  letterSpacing: '-0.02em',
};

const pendingBadgeStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: 32,
  height: 32,
  padding: '0 12px',
  borderRadius: 9999,
  background: 'rgba(245,158,11,0.15)',
  color: '#fbbf24',
  fontSize: '0.95rem',
  fontWeight: 700,
  border: '1px solid rgba(245,158,11,0.3)',
};

const statsCountStyle = {
  padding: '4px 20px 14px',
  fontSize: '0.72rem',
  color: 'rgba(255,255,255,0.35)',
};

/* Search box */
const searchWrapStyle = {
  position: 'relative',
  marginBottom: 16,
};

const searchInputStyle = {
  width: '100%',
  background: 'rgba(255,255,255,0.04)',
  border: '1px solid rgba(34,211,238,0.15)',
  borderRadius: 10,
  padding: '10px 14px 10px 38px',
  fontSize: '0.85rem',
  color: '#f1f5f9',
  outline: 'none',
  boxSizing: 'border-box',
};

const searchIconStyle = {
  position: 'absolute',
  left: 12,
  top: '50%',
  transform: 'translateY(-50%)',
  color: 'rgba(34,211,238,0.5)',
  pointerEvents: 'none',
};

/* Empty state */
const emptyStateStyle = {
  textAlign: 'center',
  padding: '60px 24px',
  background: 'rgba(255,255,255,0.02)',
  border: '1px dashed rgba(255,255,255,0.1)',
  borderRadius: 16,
  color: 'rgba(255,255,255,0.5)',
};

const emptyIconStyle = {
  fontSize: '2.5rem',
  marginBottom: 12,
  opacity: 0.4,
};

const emptyTitleStyle = {
  fontSize: '1rem',
  fontWeight: 600,
  color: 'rgba(255,255,255,0.7)',
  marginBottom: 6,
};

const emptyDescStyle = {
  fontSize: '0.8rem',
  color: 'rgba(255,255,255,0.4)',
};

/* Grid */
const gridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
  gap: 14,
};

/* Card */
const cardStyle = {
  background: 'rgba(255,255,255,0.03)',
  border: '1px solid rgba(34,211,238,0.15)',
  borderRadius: 14,
  padding: 14,
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
  transition: 'all 0.2s',
  minWidth: 0,
};

const cardTopStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  gap: 8,
};

const cardNameStyle = {
  fontSize: '0.95rem',
  fontWeight: 700,
  color: '#f1f5f9',
  letterSpacing: '-0.01em',
  marginBottom: 2,
  wordBreak: 'break-word',
};

const cardTypeStyle = {
  fontSize: '0.7rem',
  color: 'rgba(34,211,238,0.7)',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
};

const cardStatusBadgeStyle = {
  background: 'rgba(245,158,11,0.15)',
  color: '#fbbf24',
  border: '1px solid rgba(245,158,11,0.3)',
  fontSize: '0.65rem',
  fontWeight: 700,
  padding: '4px 10px',
  borderRadius: 9999,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  whiteSpace: 'nowrap',
};

const cardGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  columnGap: 10,
  rowGap: 8,
};

const cardFieldStyle = { minWidth: 0 };

const cardFieldLabelStyle = {
  fontSize: '0.62rem',
  fontWeight: 600,
  color: 'rgba(34,211,238,0.6)',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  marginBottom: 2,
};

const cardFieldValueStyle = {
  fontSize: '0.85rem',
  color: '#f1f5f9',
  fontFamily: 'monospace',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const cardPeriodStyle = {
  fontSize: '0.72rem',
  color: 'rgba(255,255,255,0.55)',
  padding: '6px 10px',
  background: 'rgba(255,255,255,0.03)',
  borderRadius: 6,
  marginTop: 2,
};

const cardActionBtnStyle = {
  marginTop: 6,
  background: 'linear-gradient(135deg, #22d3ee 0%, #3b82f6 100%)',
  color: '#0f172a',
  border: 'none',
  borderRadius: 10,
  padding: '10px 14px',
  fontSize: '0.85rem',
  fontWeight: 700,
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 8,
  boxShadow: '0 4px 14px rgba(34,211,238,0.25)',
  transition: 'all 0.2s',
};

/* Loading / error */
const loadingStyle = {
  textAlign: 'center',
  padding: '60px 24px',
  color: 'rgba(255,255,255,0.5)',
};

const errorStyle = {
  padding: 16,
  background: 'rgba(239,68,68,0.1)',
  border: '1px solid rgba(239,68,68,0.3)',
  borderRadius: 10,
  color: '#fca5a5',
  fontSize: '0.85rem',
};

/* Toast (after successful create) */
const toastStyle = {
  position: 'fixed',
  bottom: 24,
  right: 24,
  background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
  color: '#fff',
  padding: '14px 20px',
  borderRadius: 12,
  fontSize: '0.85rem',
  fontWeight: 600,
  boxShadow: '0 10px 30px rgba(0,0,0,0.4)',
  zIndex: 1100,
  display: 'flex',
  alignItems: 'center',
  gap: 10,
};


const SearchIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={searchIconStyle}>
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

const formatCurrency = (val) => (val == null || val === '' ? '$0.00' : String(val));


const PayslipCard = ({ record, onCreate }) => {
  const r = record;
  return (
    <div
      style={cardStyle}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = 'rgba(34,211,238,0.35)';
        e.currentTarget.style.background = 'rgba(34,211,238,0.04)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = 'rgba(34,211,238,0.15)';
        e.currentTarget.style.background = 'rgba(255,255,255,0.03)';
      }}
    >
      <div style={cardTopStyle}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={cardNameStyle} title={r[1]}>{r[1] || '—'}</div>
          <div style={cardTypeStyle}>{r[9] || 'TFN'} · {r[4] || '—'}</div>
        </div>
        <span style={cardStatusBadgeStyle}>Pending</span>
      </div>

      <div style={cardPeriodStyle} title={r[7]}>
        📅 {r[7] || '—'}
      </div>

      <div style={cardGridStyle}>
        <div style={cardFieldStyle}>
          <div style={cardFieldLabelStyle}>Gross</div>
          <div style={cardFieldValueStyle}>{formatCurrency(r[10])}</div>
        </div>
        <div style={cardFieldStyle}>
          <div style={cardFieldLabelStyle}>Net</div>
          <div style={cardFieldValueStyle}>{formatCurrency(r[12])}</div>
        </div>
        <div style={cardFieldStyle}>
          <div style={cardFieldLabelStyle}>PAYG</div>
          <div style={cardFieldValueStyle}>{formatCurrency(r[39])}</div>
        </div>
        <div style={cardFieldStyle}>
          <div style={cardFieldLabelStyle}>Super</div>
          <div style={cardFieldValueStyle}>{formatCurrency(r[43])}</div>
        </div>
      </div>

      <button
        type="button"
        style={cardActionBtnStyle}
        onClick={() => onCreate(r)}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-1px)';
          e.currentTarget.style.boxShadow = '0 6px 20px rgba(34,211,238,0.4)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = '0 4px 14px rgba(34,211,238,0.25)';
        }}
      >
        Create Payslip →
      </button>
    </div>
  );
};


const EmployeePayslips = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [records, setRecords] = useState([]);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);

  // Fetch pending payslips only — the backend filters server-side.
  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await fetchPayslips_pending();
      if (res.status !== 'success') throw new Error(res.error || 'Fetch failed');
      setRecords(res.pending || []);
    } catch (e) {
      setError(e.message || 'Failed to load payslips');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  // Filter by search
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return records;
    return records.filter((r) =>
      String(r[1] || '').toLowerCase().includes(q) ||
      String(r[11] || '').toLowerCase().includes(q)
    );
  }, [records, search]);

  // Stats
  const totalGross = useMemo(() =>
    records.reduce((sum, r) => {
      const v = parseFloat(String(r[10] || '0').replace(/[$,]/g, '')) || 0;
      return sum + v;
    }, 0),
    [records]
  );

  // Open modal
  const handleOpen = (record) => setSelected(record);
  const handleClose = () => {
    if (submitting) return;
    setSelected(null);
  };

  // Submit modal — POST to /tfnpayslip
  // Backend creates the PDF, uploads to S3, updates Airtable, returns JSON
  // with the S3 URL. We optimistically remove the record from local state
  // so the card disappears instantly (no waiting on Airtable propagation).
  const handleSubmit = async (formData) => {
    setSubmitting(true);
    const targetId = selected[0];
    const name = selected[1];

    try {
      const res = await fetch('/tfnpayslip', {
        method: 'POST',
        body: formData,
        credentials: 'include',
      });

      if (!res.ok) {
        const txt = await res.text();
        throw new Error(`Server error: ${txt.slice(0, 120)}`);
      }

      const data = await res.json();
      if (data.status !== 'success') {
        throw new Error(data.error || 'Unexpected response');
      }

      // Download from S3 (browser handles the actual save)
      const a = document.createElement('a');
      a.href = data.url;
      a.download = data.filename || `${name}.pdf`;
      a.target = '_blank';        // fallback if Content-Disposition isn't on the S3 object
      a.rel = 'noopener noreferrer';
      document.body.appendChild(a);
      a.click();
      a.remove();

      // Optimistic update — remove the card immediately, no Airtable race
      setRecords((prev) => prev.filter((r) => r[0] !== targetId));

      setSelected(null);
      setToast(`✓ Payslip created for ${name}`);
      setTimeout(() => setToast(null), 3500);
    } catch (e) {
      alert(`Failed to create payslip: ${e.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  /* ---------- Render ---------- */
  if (loading) {
    return (
      <div style={pageStyle}>
        <div style={loadingStyle}>Loading payslip data…</div>
      </div>
    );
  }

  return (
    <div style={pageStyle}>
      {/* Header */}
      <div style={headerWrapStyle}>
        <div style={titleBlockStyle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <h2 style={titleStyle}>Create Payslips</h2>
            <HelpButton helpData={[
              { chip: 'createpayslipsall', urlFilter: '/static/pdffilters/CreateTFNPayslips.pdf' }
            ]} />
          </div>
          <span style={subtitleStyle}>Review employee pay data, then generate PDFs</span>
        </div>
      </div>

      {pdfUrl && (
        <ContractViewer pdfUrl={pdfUrl} onClose={() => setPdfUrl(null)} />
      )}
     

      {/* Stats card */}
      <div style={statsCardStyle}>
        <div style={statsGradientBarStyle} />
        <div style={statsInnerStyle}>
          <div style={statsBlockStyle}>
            <div style={statsLabelStyle}>Total Gross Pending</div>
            <div style={statsAmountStyle}>${totalGross.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
          </div>
          <div style={{ ...statsBlockStyle, textAlign: 'right' }}>
            <div style={statsLabelStyle}>Awaiting Review</div>
            <div style={pendingBadgeStyle}>{records.length}</div>
          </div>
        </div>
        <div style={statsCountStyle}>
          {records.length} payslip{records.length !== 1 ? 's' : ''} to process
        </div>
      </div>

      {error && <div style={errorStyle}>{error}</div>}

      {/* Empty state */}
      {records.length === 0 ? (
        <div style={emptyStateStyle}>
          <div style={emptyIconStyle}>📄</div>
          <div style={emptyTitleStyle}>No payslips to create</div>
          <div style={emptyDescStyle}>All employee pay records have been processed.</div>
        </div>
      ) : (
        <>
          {/* Search */}
          <div style={searchWrapStyle}>
            <SearchIcon />
            <input
              type="text"
              placeholder="Search by name or email…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={searchInputStyle}
            />
          </div>

          {/* Grid */}
          {filtered.length === 0 ? (
            <div style={{ ...emptyStateStyle, padding: 30 }}>
              <div style={emptyDescStyle}>No records match “{search}”.</div>
            </div>
          ) : (
            <div style={gridStyle}>
              {filtered.map((r) => (
                <PayslipCard key={r[0]} record={r} onCreate={handleOpen} />
              ))}
            </div>
          )}
        </>
      )}

      {/* Modal */}
      {selected && (
        <PayslipModal
          record={selected}
          onClose={handleClose}
          onSubmit={handleSubmit}
          submitting={submitting}
        />
      )}

      {/* Toast */}
      {toast && <div style={toastStyle}>{toast}</div>}
    </div>
  );
};

export default EmployeePayslips;